import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useFeatureToggle } from '../context/FeatureToggleContext';
import { useAuth } from '../context/AuthContext';
import { useClubBranding } from '../context/ClubBrandingContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from './ui/navigation-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
  Dog,
  Menu,
  Home,
  MessageSquare,
  Calendar,
  Users,
  User,
  Mail,
  Settings,
  LogOut,
  Search,
  Sparkles,
  Briefcase,
  UserCheck,
  Trees,
  Heart,
  Activity,
  Ticket,
  Building2,
  Lightbulb,
  Vote,
  Lock,
  Map,
  FileText,
  HeartHandshake,
  TreeDeciduous,
  PawPrint,
  ChevronDown,
  Compass,
  Megaphone,
  Crown,
  Palette,
  BarChart3,
  Image,
  Stethoscope,
  Shield,
  Trophy,
  Gift,
  Newspaper,
  BookOpen,
  Link as LinkIcon,
  Globe,
  Microscope,
  LayoutDashboard,
  Tv
} from 'lucide-react';
import LanguageSelector from './LanguageSelector';
// BetaBadge no longer used in Navbar

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { isEnabled } = useFeatureToggle();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const branding = useClubBranding();

  // Grouped navigation structure
  // guestAccessible: true = publicly viewable without login (highlighted with green accent)
  const navGroups = {
    myPets: {
      label: 'My Pets',
      icon: Heart,
      items: [
        { href: '/my-pets', label: 'My Pets', icon: Heart, description: 'Manage your pet profiles' },
        { href: '/calendar', label: 'Pet Calendar', icon: Calendar, description: 'Tasks & reminders' },
        { href: '/pet-friends', label: 'Pet Friends', icon: Users, description: 'Your pets\' social connections' },
        { href: '/matchmaking', label: 'Matchmaking', icon: HeartHandshake, description: 'Find breeding matches', feature: 'matchmaking' },
        { href: '/family-tree', label: 'Family Tree', icon: TreeDeciduous, description: 'View pet ancestry' },
        { href: '/health', label: 'Health News', icon: Activity, description: 'Health tracking & articles', guestAccessible: true },
        { href: '/research', label: 'Research', icon: Microscope, description: 'Research opportunities', guestAccessible: true },
        { href: '/medical-records', label: 'Medical Records', icon: FileText, description: 'Encrypted health records', feature: 'health_records' },
        { href: '/allergens', label: 'Allergy Center', icon: Shield, description: 'Hypoallergenic breeds & compatibility' },
      ]
    },
    discover: {
      label: 'Discover',
      icon: Compass,
      items: [
        { href: '/adopt', label: 'Adopt a Pet', icon: Heart, description: 'Find your new companion', guestAccessible: true, feature: 'adoption' },
        { href: '/adoption-agencies', label: 'Rescues & Shelters', icon: Building2, description: 'Browse NGOs', guestAccessible: true, feature: 'adoption' },
        { href: '/parks', label: 'Dog Parks', icon: Trees, description: 'Find parks near you', guestAccessible: true },
        { href: '/services', label: 'Services', icon: Briefcase, description: 'Vets, groomers & more', guestAccessible: true },
        { href: '/jobs', label: 'Job Board', icon: Briefcase, description: 'Pet industry careers', guestAccessible: true },
        { href: '/organizations', label: 'Clubs', icon: Building2, description: 'Join pet organizations', guestAccessible: true },
        { href: '/map', label: 'Explore Map', icon: Map, description: 'Interactive location map', guestAccessible: true },
        { href: '/breeds', label: 'Breeds', icon: PawPrint, description: 'Breed information', guestAccessible: true },
      ]
    },
    community: {
      label: 'Community',
      icon: Users,
      items: [
        { href: '/forums', label: 'Forums', icon: MessageSquare, description: 'Community discussions', guestAccessible: true, feature: 'forums' },
        { href: '/communities', label: 'Communities', icon: Users, description: 'Topic-based sub-groups', guestAccessible: true },
        { href: '/events', label: 'Events', icon: Calendar, description: 'Pet meetups & shows', guestAccessible: true, feature: 'events' },
        { href: '/sports', label: 'Sports & Gatherings', icon: Trophy, description: 'Competitions, rallies & conventions', guestAccessible: true, feature: 'sports_events' },
        { href: '/channels', label: 'Social Channels', icon: Megaphone, description: 'Pet YouTube, TikTok & more', guestAccessible: true },
        { href: '/videos', label: 'Dog Videos', icon: Tv, description: 'Curated video content', guestAccessible: true },
        { href: '/submissions', label: 'Submit Content', icon: LinkIcon, description: 'Submit & vote on URLs' },
        { href: '/elections', label: 'Elections', icon: Vote, description: 'Community governance', feature: 'elections' },
        { href: '/pro-forums', label: 'Pro Forums', icon: Lock, description: 'Premium discussions' },
      ]
    },
    more: {
      label: 'More',
      icon: Megaphone,
      items: [
        { href: '/about', label: 'About Us', icon: Users, description: 'About our organization', guestAccessible: true },
        { href: '/club-blog', label: 'Club News', icon: Newspaper, description: 'Updates from club leadership', guestAccessible: true },
        { href: '/blog', label: 'Blog', icon: Newspaper, description: 'News & tips', guestAccessible: true, feature: 'blog' },
        { href: '/membership', label: 'Membership', icon: Crown, description: 'Membership tiers', guestAccessible: true },
        { href: '/club-resources', label: 'Club Resources', icon: BookOpen, description: 'Member-only resources' },
        { href: '/creative', label: 'Creative Shop', icon: Palette, description: 'Buy & sell pet art' },
        { href: '/products', label: 'Product Reviews', icon: BarChart3, description: 'Compare pet products', guestAccessible: true },
        { href: '/albums', label: 'Media Albums', icon: Image, description: 'Photos & memories' },
        { href: '/ai-health', label: 'AI Health', icon: Stethoscope, description: 'AI health detection' },
        { href: '/deals', label: 'Deals', icon: Ticket, description: 'Partner discounts', guestAccessible: true },
        { href: '/recommendations', label: 'Resources', icon: Lightbulb, description: 'Community recommendations', guestAccessible: true },
        { href: '/roadmap', label: 'Roadmap', icon: Map, description: 'Feature roadmap', guestAccessible: true },
      ]
    }
  };

  // Filter nav items by enabled features
  const filteredNavGroups = Object.fromEntries(
    Object.entries(navGroups).map(([key, group]) => [
      key,
      {
        ...group,
        items: group.items.filter(item => !item.feature || isEnabled(item.feature))
      }
    ])
  );

  // Flat list for mobile menu
  const allNavLinks = Object.values(filteredNavGroups).flatMap(group => group.items);

  // Guest-accessible items for the guest menu
  const guestAccessibleItems = Object.values(filteredNavGroups)
    .flatMap(group => group.items)
    .filter(item => item.guestAccessible);

  const isActive = (path) => location.pathname === path;
  const isGroupActive = (group) => group.items.some(item => location.pathname === item.href);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Navigation menu item component
  const NavMenuItem = ({ item, onClick }) => (
    <Link
      to={item.href}
      onClick={onClick}
      className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
    >
      <div className="flex items-center gap-2">
        <item.icon className={`w-4 h-4 ${item.guestAccessible ? 'text-emerald-600' : 'text-muted-foreground'}`} />
        <div className="text-sm font-medium leading-none">{item.label}</div>
        {item.guestAccessible && (
          <Globe className="w-3 h-3 text-emerald-600 ml-auto" title="Available to guests" />
        )}
      </div>
      <p className="line-clamp-1 text-xs leading-snug text-muted-foreground mt-1">
        {item.description}
      </p>
    </Link>
  );

  return (
    <header className="glass-nav sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo - Dynamic based on domain */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center group-hover:scale-105 transition-transform ${branding.isWorldPets ? 'bg-emerald-600' : 'bg-primary'}`}>
              {branding.isWorldPets ? (
                <Globe className="w-5 h-5 text-white" />
              ) : (
                <Dog className="w-5 h-5 text-primary-foreground" />
              )}
            </div>
            <span className="font-heading text-xl font-semibold hidden sm:block">
              {branding.siteName}<span className={branding.isWorldPets ? 'text-emerald-600' : 'text-secondary'}> {branding.siteNameSecondary}</span>
            </span>
          </Link>

          {/* Desktop Navigation with Dropdowns */}
          {user && (
            <NavigationMenu className="hidden lg:flex">
              <NavigationMenuList>
                {/* Feed - Direct Link */}
                <NavigationMenuItem>
                  <Link
                    to="/feed"
                    className={`group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground ${
                      isActive('/feed') ? 'bg-accent text-accent-foreground' : ''
                    }`}
                    data-testid="nav-feed"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Feed
                  </Link>
                </NavigationMenuItem>

                {/* My Pets Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={isGroupActive(filteredNavGroups.myPets) ? 'bg-accent text-accent-foreground' : ''}
                    data-testid="nav-my-pets-dropdown"
                  >
                    <Heart className="w-4 h-4 mr-2" />
                    My Pets
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-1 p-2 md:w-[500px] md:grid-cols-2">
                      {filteredNavGroups.myPets.items.map((item) => (
                        <li key={item.href}>
                          <NavigationMenuLink asChild>
                            <NavMenuItem item={item} />
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Discover Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={isGroupActive(filteredNavGroups.discover) ? 'bg-accent text-accent-foreground' : ''}
                    data-testid="nav-discover-dropdown"
                  >
                    <Compass className="w-4 h-4 mr-2" />
                    Discover
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-1 p-2 md:w-[500px] md:grid-cols-2">
                      {filteredNavGroups.discover.items.map((item) => (
                        <li key={item.href}>
                          <NavigationMenuLink asChild>
                            <NavMenuItem item={item} />
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Community Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={isGroupActive(filteredNavGroups.community) ? 'bg-accent text-accent-foreground' : ''}
                    data-testid="nav-community-dropdown"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Community
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[350px] gap-1 p-2">
                      {filteredNavGroups.community.items.map((item) => (
                        <li key={item.href}>
                          <NavigationMenuLink asChild>
                            <NavMenuItem item={item} />
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* More Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={isGroupActive(filteredNavGroups.more) ? 'bg-accent text-accent-foreground' : ''}
                    data-testid="nav-more-dropdown"
                  >
                    <Megaphone className="w-4 h-4 mr-2" />
                    More
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[300px] gap-1 p-2">
                      {filteredNavGroups.more.items.map((item) => (
                        <li key={item.href}>
                          <NavigationMenuLink asChild>
                            <NavMenuItem item={item} />
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Feedback - Direct Link */}
                <NavigationMenuItem>
                  <Link
                    to={`/beta-feedback?from=${encodeURIComponent(location.pathname)}`}
                    className={`group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground ${
                      isActive('/beta-feedback') ? 'bg-accent text-accent-foreground' : ''
                    }`}
                    data-testid="nav-beta-feedback"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Feedback
                  </Link>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          )}

          {/* Right Section */}
          <div className="flex items-center gap-2">
            {/* Language Selector - Always visible */}
            <div className="hidden md:block">
              <LanguageSelector variant="compact" />
            </div>

            {user ? (
              <>
                {/* Search Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="hidden md:flex"
                  onClick={() => navigate('/search')}
                  data-testid="nav-search"
                >
                  <Search className="w-5 h-5" />
                </Button>

                {/* Messages */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="hidden md:flex"
                  onClick={() => navigate('/messages')}
                  data-testid="nav-messages"
                >
                  <Mail className="w-5 h-5" />
                </Button>

                {/* Profile - Direct Link */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="hidden md:flex"
                  onClick={() => navigate(`/profile/${user.username}`)}
                  data-testid="nav-profile-direct"
                >
                  <User className="w-5 h-5" />
                </Button>

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="nav-user-menu">
                      <Avatar className="h-10 w-10 border-2 border-primary/20">
                        <AvatarImage src={user.avatar_url} alt={user.display_name} />
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {user.display_name?.charAt(0) || user.username?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="end">
                    <div className="flex items-center gap-3 p-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={user.avatar_url} />
                        <AvatarFallback>{user.display_name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="font-medium">{user.display_name}</span>
                        <span className="text-xs text-muted-foreground">@{user.username}</span>
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">Community</DropdownMenuLabel>
                    <DropdownMenuItem onClick={() => navigate(`/beta-feedback?from=${encodeURIComponent(location.pathname)}`)} data-testid="menu-beta-feedback">
                      <MessageSquare className="mr-2 h-4 w-4" />
                      Feedback & Updates
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate(`/profile/${user.username}`)} data-testid="menu-profile">
                      <User className="mr-2 h-4 w-4" />
                      My Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      My Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/my-promo-page')} data-testid="menu-promo-page">
                      <Crown className="mr-2 h-4 w-4" />
                      My Promo Page
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/referrals')} data-testid="menu-referrals">
                      <Gift className="mr-2 h-4 w-4" />
                      Referral Program
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                      <Settings className="mr-2 h-4 w-4" />
                      Settings & Preferences
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/friends')} data-testid="menu-friends">
                      <UserCheck className="mr-2 h-4 w-4" />
                      Friends
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/messages')} data-testid="menu-messages">
                      <Mail className="mr-2 h-4 w-4" />
                      Messages
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/interests')} data-testid="menu-interests">
                      <Sparkles className="mr-2 h-4 w-4" />
                      Interests & Ads
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {user?.membership_tier === 'premium' && (
                      <DropdownMenuItem onClick={() => navigate('/creator-dashboard')} data-testid="menu-creator-dashboard">
                        <Crown className="mr-2 h-4 w-4 text-amber-500" />
                        Creator Dashboard
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => navigate('/club-admin')} data-testid="menu-club-admin">
                      <Settings className="mr-2 h-4 w-4" />
                      Club Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                      <Settings className="mr-2 h-4 w-4" />
                      Admin Settings
                    </DropdownMenuItem>
                    {(user?.is_platform_admin || user?.is_sysadmin) && (
                      <DropdownMenuItem onClick={() => navigate('/sysadmin')} data-testid="menu-sysadmin">
                        <Shield className="mr-2 h-4 w-4" />
                        Sysadmin Dashboard
                      </DropdownMenuItem>
                    )}
                    {(user?.is_admin || user?.is_operator_admin || user?.is_platform_admin || user?.is_sysadmin) && (
                      <DropdownMenuItem onClick={() => navigate('/vbb/operations')} data-testid="menu-ops">
                        <Briefcase className="mr-2 h-4 w-4" />
                        Operations Center
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                      <LogOut className="mr-2 h-4 w-4" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Mobile Menu */}
                <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                  <SheetTrigger asChild className="lg:hidden">
                    <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                      <Menu className="h-6 w-6" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-80 overflow-y-auto">
                    <nav className="flex flex-col gap-1 mt-8">
                      {/* Language Selector for Mobile Users */}
                      <div className="px-4 pb-4 mb-2 border-b">
                        <p className="text-xs text-muted-foreground mb-2">Language</p>
                        <LanguageSelector variant="default" showLabel={true} />
                      </div>
                      
                      {/* Feed direct link */}
                      <Link
                        to="/feed"
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                          isActive('/feed')
                            ? 'bg-primary text-primary-foreground'
                            : 'hover:bg-muted'
                        }`}
                      >
                        <Home className="w-5 h-5" />
                        Feed
                      </Link>

                      {/* Grouped items for mobile */}
                      {Object.entries(filteredNavGroups).map(([key, group]) => (
                        <div key={key} className="mt-4">
                          <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            {group.label}
                          </div>
                          {group.items.map((item) => (
                            <Link
                              key={item.href}
                              to={item.href}
                              onClick={() => setMobileOpen(false)}
                              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                                isActive(item.href)
                                  ? 'bg-primary text-primary-foreground'
                                  : 'hover:bg-muted'
                              }`}
                            >
                              <item.icon className={`w-5 h-5 ${item.guestAccessible && !isActive(item.href) ? 'text-emerald-600' : ''}`} />
                              {item.label}
                              {item.guestAccessible && !isActive(item.href) && (
                                <Globe className="w-3 h-3 text-emerald-600 ml-auto" />
                              )}
                            </Link>
                          ))}
                        </div>
                      ))}

                      {/* Profile link in mobile */}
                      <div className="mt-4 pt-4 border-t">
                        <Link
                          to={`/beta-feedback?from=${encodeURIComponent(location.pathname)}`}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted"
                        >
                          <MessageSquare className="h-4 w-4" />
                          Feedback & Updates
                        </Link>
                        <Link
                          to={`/profile/${user.username}`}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted"
                        >
                          <User className="w-5 h-5" />
                          My Profile
                        </Link>
                        <Link
                          to="/dashboard"
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted"
                          data-testid="mobile-menu-dashboard"
                        >
                          <LayoutDashboard className="w-5 h-5" />
                          My Dashboard
                        </Link>
                        <Link
                          to="/settings"
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted"
                        >
                          <Settings className="w-5 h-5" />
                          Settings
                        </Link>
                      </div>
                    </nav>
                  </SheetContent>
                </Sheet>
              </>
            ) : (
              /* Guest Navigation */
              <div className="flex items-center gap-2">
                {/* Guest Menu - Desktop Dropdown */}
                <NavigationMenu className="hidden md:flex">
                  <NavigationMenuList>
                    <NavigationMenuItem>
                      <NavigationMenuTrigger 
                        className="bg-emerald-600/10 text-emerald-700 border border-emerald-200 hover:bg-emerald-600/20"
                        data-testid="nav-guest-menu"
                      >
                        <Globe className="w-4 h-4 mr-2" />
                        Explore
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <div className="p-4 w-[500px]">
                          <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                            <Globe className="w-4 h-4 text-emerald-600" />
                            <span className="text-sm font-medium text-emerald-700">Open to Everyone</span>
                          </div>
                          <ul className="grid grid-cols-2 gap-1">
                            {guestAccessibleItems.map((item) => (
                              <li key={item.href}>
                                <NavigationMenuLink asChild>
                                  <NavMenuItem item={item} />
                                </NavigationMenuLink>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  </NavigationMenuList>
                </NavigationMenu>

                {/* Mobile Guest Menu */}
                <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                  <SheetTrigger asChild className="md:hidden">
                    <Button variant="outline" size="sm" className="border-emerald-200 text-emerald-700" data-testid="mobile-guest-menu-trigger">
                      <Menu className="h-5 w-5 mr-1" />
                      Explore
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-80 overflow-y-auto">
                    <nav className="flex flex-col gap-1 mt-8">
                      {/* Language Selector for Mobile Guests */}
                      <div className="px-4 pb-4 mb-2 border-b">
                        <p className="text-xs text-muted-foreground mb-2">Language</p>
                        <LanguageSelector variant="default" showLabel={true} />
                      </div>
                      
                      <div className="flex items-center gap-2 px-4 py-2 mb-2">
                        <Globe className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm font-medium text-emerald-700">Open to Everyone</span>
                      </div>
                      {guestAccessibleItems.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          onClick={() => setMobileOpen(false)}
                          className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                            isActive(item.href)
                              ? 'bg-emerald-600 text-white'
                              : 'hover:bg-emerald-50 text-emerald-700'
                          }`}
                        >
                          <item.icon className="w-5 h-5" />
                          {item.label}
                        </Link>
                      ))}
                      
                      {/* Auth buttons in mobile sheet */}
                      <div className="mt-6 pt-4 border-t px-4 space-y-2">
                        <Button className="w-full btn-primary" onClick={() => { setMobileOpen(false); navigate('/register'); }} data-testid="mobile-register">
                          Join Now
                        </Button>
                        <Button variant="outline" className="w-full" onClick={() => { setMobileOpen(false); navigate('/login'); }} data-testid="mobile-login">
                          Log in
                        </Button>
                      </div>
                    </nav>
                  </SheetContent>
                </Sheet>

                {/* Language Selector for Desktop Guests */}
                <div className="hidden md:block">
                  <LanguageSelector variant="compact" />
                </div>

                <Button variant="ghost" onClick={() => navigate('/login')} data-testid="nav-login">
                  Log in
                </Button>
                <Button className="btn-primary" onClick={() => navigate('/register')} data-testid="nav-register">
                  Join Now
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
